'use client';
import { trackEcommerce, trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import type { Role } from '@svenskaspelab/nova-core/nextjs/auth';
import { useDialog } from '@svenskaspelab/nova-foundation/nextjs/dialog';
import { deleteCoupon, resetCoupon } from '@svenskaspelab/poolbetting/core/action/coupon';
import {
  getMarketplaceBalance,
  getRetailerBalance,
} from '@svenskaspelab/poolbetting/core/frameworks/nextjs/marketplace';
import {
  useCouponId,
  useCouponStatus,
  usePlaceCouponWager,
  usePlaceExternalCouponWager,
} from '@svenskaspelab/poolbetting/core/nextjs/coupon';
import { type Coupon, getCouponIndex } from '@svenskaspelab/poolbetting/schema/coupon';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { Action, Commission } from '@svenskaspelab/poolbetting/schema/input';
import type {
  ActivatedDrawId,
  MarketplaceBalance,
  RetailerBalanceResult,
} from '@svenskaspelab/poolbetting/schema/marketplace';
import type { ExternalWagerGuid } from '@svenskaspelab/poolbetting/schema/wager';
import { CouponForm } from '@svenskaspelab/poolbetting/ui/CouponForm';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  type PropsWithChildren,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { walletCachedContext } from '@/components/index.browser';

type CouponFormProps = PropsWithChildren & {
  action?: Action;
  commissionPercent?: Commission;
  activatedDrawId?: ActivatedDrawId;
  paymentUrls?: Record<string, string | undefined>;
  marketplaceWallets: MarketplaceBalance | RetailerBalanceResult | undefined;
  drawNumber: DrawNumber;
  product: Definition['drawProducts'][number];
  role?: Role;
  externalCoupon?: Coupon;
  externalWagerGuid?: ExternalWagerGuid;
};

/**
 * Coupon form manager that wraps component in a form to handle potential server actions.
 *
 * @param root0 props
 * @param root0.children jsx children
 * @param root0.commissionPercent optional commission percentage to be applied to the wager, used for retailer wallet case
 * @param root0.activatedDrawId optional activated draw id, used for retailer wallet case
 * @param root0.paymentUrls optional payment urls, used for redirecting to payment page in retailer wallet case
 * @param root0.marketplaceWallets optional wallet info, used for retailer wallet case
 * @param root0.drawNumber draw number, used for wallet case to get current balance
 * @param root0.product product module engine, used for wallet case to get current balance
 * @param root0.role user role, used to determine if the user is player or retailer, which affects how wallet balance is fetched and updated
 * @param root0.externalCoupon optional external coupon data, if provided external wager flow is used
 * @param root0.externalWagerGuid optional external wager guid, used for updating the external wager after successful placement
 * @param root0.action optional coupon action, used to determine the action to be taken for the coupon
 * @returns a manager that handles form for coupon
 */
export default function CouponFormManager({
  children,
  commissionPercent,
  activatedDrawId,
  paymentUrls,
  marketplaceWallets,
  drawNumber,
  product,
  role,
  externalCoupon,
  externalWagerGuid,
  action,
}: CouponFormProps): ReactNode {
  const [couponId, selectedCouponIndex] = useCouponId();
  // eslint-disable-next-line react/hook-use-state
  const [wallet] = useState(marketplaceWallets);
  const [shouldRefetchWallet, setShouldRefetchWallet] = useState(false);
  const hasPendingPayment = useRef(false);
  const hasInitialWalletSet = useRef(false);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const couponStatus = useCouponStatus({ couponId, couponIndex: selectedCouponIndex });

  const { context: walletContext } = walletCachedContext;
  const context = useContext(walletContext);
  useEffect(() => {
    if (context?.setState && wallet && !hasInitialWalletSet.current) {
      context.setState(wallet);
      hasInitialWalletSet.current = true;
    }
  }, [wallet, context]);
  const marketplacePaymentWallets: Record<
    ActivatedDrawId,
    { balance: number; soldShares: number; sharePrice: number }
  > = {};
  if (context?.state && 'activatedDraws' in context.state) {
    for (const draw of context.state.activatedDraws) {
      const { balance, activatedDrawId: walletActivatedDrawId } = draw;
      const sharePrice = Number.parseInt(draw.SharePrice, 10);
      const soldShares = balance / sharePrice;
      marketplacePaymentWallets[walletActivatedDrawId] = {
        balance,
        soldShares,
        sharePrice,
      };
    }
  }
  if (context?.state && 'activatedDraw' in context.state && context.state.activatedDraw) {
    const {
      currentPlayableAmount,
      sharePrice,
      soldShares,
      activatedDrawId: retailerActivatedDrawId,
    } = context.state.activatedDraw;
    const balance = Number.parseInt(currentPlayableAmount, 10);
    const sharePriceNum = Number.parseInt(sharePrice, 10);
    marketplacePaymentWallets[retailerActivatedDrawId] = {
      balance,
      soldShares: soldShares,
      sharePrice: sharePriceNum,
    };
  }

  const callbackOnPaymentMpShares = (): void => {
    if (context?.state !== undefined) {
      setShouldRefetchWallet(true);
    }
  };

  const callbackOnPaymentDone = (): void => {
    hasPendingPayment.current = false;
  };

  useEffect(() => {
    const refetchWalletData = async (): Promise<void> => {
      setShouldRefetchWallet(false);
      if (role === 'Player') {
        const result = await getMarketplaceBalance({ product, drawNumber });
        if (result.status === 'SUCCESS') {
          context?.setState(result.data);
        }
      }
      if (role === 'Retailer' && activatedDrawId) {
        const result = await getRetailerBalance({ activatedDrawId });
        if (result.status === 'SUCCESS' && result.data.activatedDraw) {
          context?.setState(result.data);
        }
      }
    };

    if (shouldRefetchWallet) {
      void refetchWalletData();
    }
  }, [shouldRefetchWallet, product, drawNumber, context, role, activatedDrawId]);

  const { mutate: mutateCouponWager } = usePlaceCouponWager({
    commissionPercent,
    activatedDrawId,
    paymentUrls,
    marketplaceWallets: marketplacePaymentWallets,
    callbackOnPaymentDone,
    callbackOnPaymentMpShares,
    role,
    trackTransaction: trackEcommerce,
    trackEventFunction: trackEvent,
  });

  const { mutate: mutateExternalCouponWager } = usePlaceExternalCouponWager({
    activatedDrawId,
    paymentUrls,
    marketplaceWallets: marketplacePaymentWallets,
    callbackOnPaymentDone,
    callbackOnPaymentMpShares,
    externalWagerGuid,
    role,
    trackTransaction: trackEcommerce,
    trackEventFunction: trackEvent,
  });
  const { showDialog } = useDialog();

  /*
   * Use onSubmit to handle form submission instead
   */
  const handleSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void => {
      event.preventDefault();

      if (hasPendingPayment.current) {
        return;
      }

      if (!(
        event.nativeEvent.submitter instanceof HTMLButtonElement ||
        event.nativeEvent.submitter instanceof HTMLInputElement
      )) {
        return;
      }

      const { submitter } = event.nativeEvent;

      if (submitter.name === 'delete') {
        void (async (): Promise<void> => {
          const couponIndex = Number.parseInt(submitter.value, 10);
          if (!getCouponIndex(couponIndex)) {
            return;
          }

          trackEvent({ category: 'Nya tipsen', action: 'Ta bort kupong', optLabel: pathname });
          await deleteCoupon({ couponId, couponIndex });
        })();
        return;
      }

      if (submitter.name === 'clear') {
        void (async (): Promise<void> => {
          const couponIndex = Number.parseInt(submitter.value, 10);
          if (!getCouponIndex(couponIndex)) {
            return;
          }
          await resetCoupon({ couponId, couponIndex });
        })();
        return;
      }

      hasPendingPayment.current = true;

      if (externalCoupon) {
        mutateExternalCouponWager({ coupon: externalCoupon, showDialog });
        return;
      }

      mutateCouponWager({ couponId, showDialog });
    },
    [couponId, externalCoupon, mutateCouponWager, mutateExternalCouponWager, pathname, showDialog]
  );

  useEffect(() => {
    if (action !== 'pay' || couponStatus !== 'VALID') {
      return;
    }

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });

    // Drop the param first so a refresh or re-render cannot retrigger the payment.
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('action');
    const query = nextSearchParams.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });

    const form = document.querySelector<HTMLFormElement>('#coupon_form');
    const payButton = document.querySelector<HTMLButtonElement>('#coupon_form_pay');
    if (form && payButton) {
      form.requestSubmit(payButton);
    }
  }, [action, couponStatus, pathname, router, searchParams]);

  return (
    <CouponForm onSubmit={handleSubmit} id="coupon_form">
      {children}
    </CouponForm>
  );
}
